import { useSuspenseQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchOrganizationEstablishmentsQueryOptions } from "@/api/establishments";
import { fetchScheduleByIdQueryOptions } from "@/api/schedules";
import { fetchOrganizationStaffQueryOptions } from "@/api/staff";
import { Button } from "@/components/ui/button";
import { useApiParameters } from "@/hooks/useApiParameters";
import { ScheduleDeletionConfirmationModal } from "../-components/ScheduleDeletionConfirmationModal";
import { ScheduleForm } from "../-components/ScheduleForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/schedules/$scheduleId/",
)({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, scheduleId } = params;

		// Preload all required data
		context.queryClient.ensureQueryData(
			fetchOrganizationEstablishmentsQueryOptions(userId, token, orgId),
		);
		context.queryClient.ensureQueryData(
			fetchOrganizationStaffQueryOptions(token, orgId),
		);
		context.queryClient.ensureQueryData(
			fetchScheduleByIdQueryOptions(userId, token, orgId, scheduleId),
		);

		return { userId, token, orgId, scheduleId };
	},
	component: ScheduleDetailsPage,
});

function ScheduleDetailsPage() {
	const { t } = useTranslation();
	const { orgId, scheduleId } = useParams({
		from: "/_auth/app/_app/org/$orgId/schedules/$scheduleId/",
	});
	const { userId, token } = Route.useLoaderData();
	const navigate = useNavigate();

	// Archive / delete modal state (the modal owns its own mutations)
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	// Get all required data
	const { data: establishments } = useSuspenseQuery(
		fetchOrganizationEstablishmentsQueryOptions(userId, token, orgId),
	);
	const { data: staff } = useSuspenseQuery(
		fetchOrganizationStaffQueryOptions(token, orgId),
	);
	const { data: schedule } = useSuspenseQuery(
		fetchScheduleByIdQueryOptions(userId, token, orgId, scheduleId),
	);

	// Handle successful form submission
	const handleSuccess = () => {
		navigate({
			to: "/app/org/$orgId/schedules",
			params: { orgId },
		});
	};

	// Open the archive / delete modal
	const handleDeleteClick = useCallback(() => {
		setShowDeleteModal(true);
	}, []);

	// Called by the modal after a successful archive or delete
	const handleModalSuccess = useCallback(() => {
		setShowDeleteModal(false);
		navigate({
			to: "/app/org/$orgId/schedules",
			params: { orgId },
		});
	}, [navigate, orgId]);

	const handleDeleteCancel = useCallback(() => {
		setShowDeleteModal(false);
	}, []);

	return (
		<>
			<div className="h-full flex flex-col overflow-hidden">
				<ScheduleForm
					mode="edit"
					orgId={orgId}
					token={token}
					establishments={establishments}
					staff={staff}
					initialData={schedule}
					onSuccess={handleSuccess}
					deleteButton={
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="text-error hover:bg-error/10 hover:text-error"
							onClick={handleDeleteClick}
							data-testid="delete-schedule-button"
						>
							<Trash2 className="size-4" />
							<span className="sr-only sm:not-sr-only">
								{t("common.delete")}
							</span>
						</Button>
					}
				/>
			</div>

			{/* Schedule Deletion Confirmation Modal */}
			<ScheduleDeletionConfirmationModal
				isOpen={showDeleteModal}
				onClose={handleDeleteCancel}
				onSuccess={handleModalSuccess}
				schedule={schedule}
				organizationId={orgId}
				token={token}
			/>
		</>
	);
}
